import { useI18n } from "vue-i18n";
import { useFormStore } from "@/features/blueprint/stores/useFormStore";
import { Answers } from "@/features/blueprint/schema/answers/Answers";
import { useScoreStore } from "@/features/blueprint/stores/useScoreStore";

import { computed } from "vue";

export function useBlueprintSummary(answers: Answers) {
    const { t } = useI18n();
    const formStore = useFormStore();
    const scoreStore = useScoreStore();
    const totalScore = scoreStore.progress;

    const summaryKey = computed(() => {
        if (totalScore === 100) return "ui.score_summary_perfect";
        if (totalScore >= 90) return "ui.score_summary_excellent";
        if (totalScore >= 75) return "ui.score_summary_strong";
        if (totalScore >= 50) return "ui.score_summary_medium";
        if (totalScore >= 30) return "ui.score_summary_early";
        return "ui.score_summary_start";
    });

    const headlineKey = computed(() => {
        if (totalScore === 100) return "ui.score_title_perfect";
        if (totalScore >= 90) return "ui.score_title_excellent";
        if (totalScore >= 75) return "ui.score_title_strong";
        if (totalScore >= 50) return "ui.score_title_medium";
        if (totalScore >= 30) return "ui.score_title_early";
        return "ui.score_title_start";
    });

    function isNonEmptyAnswer(value: unknown): boolean {
        return value !== undefined && value !== "";
    }

    function retrieveAnswer(questionId: string): unknown {
        return answers[questionId];
    }

    function booleanBadgeClass(questionId: string): string {
        return retrieveAnswer(questionId) ? "badge-success" : "badge-error";
    }

    function getBooleanLabel(questionId: string): string {
        return retrieveAnswer(questionId) ? t("ui.yes") : t("ui.no");
    }

    function listAnswerItems(questionId: string): string[] {
        const val = retrieveAnswer(questionId);
        return Array.isArray(val) ? val : [];
    }

    function colorValues(questionId: string): string[] {
        const val = retrieveAnswer(questionId);
        if (Array.isArray(val)) return val;
        return typeof val === "string" ? [val] : [];
    }

    function navigateToSection(sectionIndex: number): void {
        formStore.currentSectionIndex = sectionIndex;
        formStore.saveToLocalStorage();
    }

    function openEmailClient(): void {
        const subject = encodeURIComponent(t("ui.email_subject", { score: totalScore }));
        const body = encodeURIComponent(t("ui.email_body"));
        window.location.href = `mailto:?subject=${subject}&body=${body}`;
    }

    return {
        t,
        totalScore,
        summaryKey,
        headlineKey,
        isNonEmptyAnswer,
        retrieveAnswer,
        booleanBadgeClass,
        getBooleanLabel,
        listAnswerItems,
        colorValues,
        navigateToSection,
        openEmailClient
    };
}
